/**
 * Utility helper functions for Quill's editor and delta
 *
 * Copyright (c) 2019 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import Delta from 'quill-delta'
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html'

/**
 * Converts the provided content into a Delta if it's not empty, otherwise returns null
 * @param {Array|Object} content The array of operations or an object with an ops key set to an array of operations
 */
const _convertToNotEmptyDelta = (content = []) => {
  const delta = new Delta(content)

  if (delta.length() === 0) {
    return null
  } else if (JSON.stringify(delta) === '{"ops":[{"insert":"\\n"}]}') {
    return null
  }

  return delta
}

/**
 * Checks whether the provided content is considered "empty"
 * Somewhat janky solution because if you "click into" an editor, it will always insert a new line
 * https://github.com/quilljs/quill/issues/163
 * @param {Array|Object} content The array of operations or an object with an ops key set to an array of operations
 */
export const isEmpty = (content = []) => {
  // Checks if the content was an old slate document, if so, automatically ignore it
  if (isSlateObj(content)) {
    return true
  }

  const delta = _convertToNotEmptyDelta(content)

  return !delta
}

/**
 * Checks if the provided value is a slate document, and if so, checks whether it's empty
 * This function exists soley for backwards compatibility
 * @param {Object} value The Slate editor document
 */
export const isSlateObj = (value) => {
  if (value?.object === 'value') {
    return true
  }

  return false
}

/**
 * Converts the provided content into a Delta, and then return the HTNL output
 * @param {Array|Object} content The array of operations or an object with an ops key set to an array of operations
 */
export const deltaToHtml = (content = []) => {
  const delta = _convertToNotEmptyDelta(content)

  if (!delta) {
    return ''
  }

  const converter = new QuillDeltaToHtmlConverter(delta.ops)

  return converter.convert()
}

/**
 * Checks whether the provided content only consists of link or '\n' attributes
    "ops": [
      {
        "attributes": {
          "link": "https://test.com"
        },
        "insert": "https://test.com"
      },
      {
        "insert": "\n"
      }
    ]
 * @param {Array|Object} content The array of operations or an object with an ops key set to an array of operations
 */
export const isLinkNote = (content = []) => {
  const delta = _convertToNotEmptyDelta(content)

  if (delta) {
    const ops = delta.ops

    // If we find an operation that is not a link and is not just a series of empty lines (\n), then this is not a link only note
    const index = ops.findIndex(({ attributes, insert }) => (
      // Replace all new lines with an empty string
      !attributes?.link && (insert && insert.replace(/[\n]/g, '').length > 0))
    )

    if (index < 0) {
      return true
    }
  }

  return false
}
